import React, { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  Menu,
  X,
  Network,
  Layers,
  Handshake,
  Boxes,
  Users,
  ShieldCheck,
  Target,
  Sparkles,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Activity,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ChartLine,
  Briefcase,
  Cpu,
} from "lucide-react";
import {
  NAV,
  HERO,
  SERVICES,
  WHY,
  INDUSTRIES,
  PMOTOOL,
  STATS,
  VISION,
  CONTACT,
  FOOTER,
} from "@/constants/testIds/pmonexa";
import ViniChat from "@/components/ViniChat";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_full-stack-site-21/artifacts/c0g2l9fv_IMG_8230.jpeg";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------- Data ----------
const NAV_LINKS = [
  { label: "Home", href: "#home", testId: NAV.home },
  { label: "About", href: "#about", testId: NAV.about },
  { label: "Services", href: "#services", testId: NAV.services },
  { label: "PMO Tool", href: "#pmo-tool", testId: NAV.pmoTool },
  { label: "Vendor Mgmt", href: "#services", testId: NAV.vendor },
  { label: "Staffing", href: "#services", testId: NAV.staffing },
  { label: "Insights", href: "#why", testId: NAV.insights },
  { label: "Contact", href: "#contact", testId: NAV.contact },
];

const SERVICES_DATA = [
  {
    slug: "pmo-advisory",
    code: "01",
    title: "PMO Advisory & Consulting",
    desc: "Stand up high-performing PMOs, mature existing ones, and rescue programs in distress. Governance, portfolio, and turnaround built for enterprise scale.",
    bullets: [
      "PMO setup & maturity assessment",
      "Project governance frameworks",
      "Portfolio & program management",
      "Project recovery & turnaround",
    ],
    icon: Network,
    span: "lg:col-span-8 lg:row-span-2",
    featured: true,
    metrics: [
      { value: "4-6", label: "Weeks to stand-up" },
      { value: "5", label: "Maturity levels" },
      { value: "100+", label: "Reusable playbooks" },
    ],
    frameworks: ["PMI / PMBOK", "PRINCE2", "SAFe", "Agile / Scrum", "Lean Six Sigma"],
    phases: [
      { code: "01", label: "Diagnose" },
      { code: "02", label: "Design" },
      { code: "03", label: "Deploy" },
      { code: "04", label: "Drive" },
    ],
  },
  {
    slug: "pmo-tool",
    code: "02",
    title: "Lightweight PMO Tool",
    desc: "A purpose-built control room for executive visibility — without the bloat of legacy PPM suites.",
    bullets: [
      "Portfolio dashboard",
      "Risk & issue management",
      "Resource & vendor tracking",
    ],
    icon: Layers,
    span: "lg:col-span-4 lg:row-span-1",
  },
  {
    slug: "vendor",
    code: "03",
    title: "Vendor Management",
    desc: "Onboarding, SLA & KPI monitoring, contract support, and quarterly performance reviews.",
    bullets: ["Onboarding & governance", "SLA / KPI monitoring", "Contract support"],
    icon: Handshake,
    span: "lg:col-span-4 lg:row-span-1",
  },
  {
    slug: "app-rationalization",
    code: "04",
    title: "Application Rationalization",
    desc: "Trim cost and complexity from your app estate. Consolidate, modernize, and retire legacy systems with confidence.",
    bullets: [
      "Portfolio assessment",
      "Cost optimization",
      "Technology consolidation",
      "Legacy retirement planning",
    ],
    icon: Boxes,
    span: "lg:col-span-6 lg:row-span-1",
  },
  {
    slug: "staffing",
    code: "05",
    title: "Staffing & Resource Augmentation",
    desc: "On-demand PMO professionals, SAP consultants, PMs, BAs, and transformation specialists — vetted and ready.",
    bullets: ["PMO professionals", "SAP consultants", "BAs & Transformation leads"],
    icon: Users,
    span: "lg:col-span-6 lg:row-span-1",
  },
];

const WHY_PILLARS = [
  {
    slug: "expertise",
    icon: ShieldCheck,
    title: "Industry Expertise",
    desc: "Twenty plus years across manufacturing, chemicals, pharmaceuticals, shared services and global IT operations.",
    metric: "20+",
    metricLabel: "Years",
  },
  {
    slug: "delivery",
    icon: Activity,
    title: "Proven Delivery",
    desc: "Hands-on leadership of SAP programs, digital transformation, rationalization, and enterprise PMOs.",
    metric: "07+",
    metricLabel: "Programs",
  },
  {
    slug: "outcomes",
    icon: Target,
    title: "Business-Focused",
    desc: "Every technology investment aligned to measurable ROI and sustainable enterprise growth.",
    metric: "3.4x",
    metricLabel: "Avg ROI",
  },
];

const INDUSTRIES_DATA = [
  { slug: "manufacturing", name: "Manufacturing", tag: "Operations / OT" },
  { slug: "chemicals", name: "Chemicals", tag: "Compliance / Safety" },
  { slug: "pharmaceuticals", name: "Pharmaceuticals", tag: "GxP / Validation" },
  { slug: "logistics", name: "Logistics & Supply Chain", tag: "S/4 + WMS" },
  { slug: "shared-services", name: "Shared Services", tag: "Process Excellence" },
  { slug: "global-enterprises", name: "Global Enterprises", tag: "Multi-region PMO" },
];

const PMO_TOOL_FEATURES = [
  { slug: "portfolio", icon: ChartLine, title: "Portfolio Dashboard", note: "Real-time program health & burn-up." },
  { slug: "risks", icon: AlertTriangle, title: "Risk & Issue Register", note: "Auto-aggregated heatmaps." },
  { slug: "resources", icon: Users, title: "Resource Tracking", note: "Capacity vs. demand at a glance." },
  { slug: "exec", icon: Briefcase, title: "Executive Reporting", note: "Board-ready, in two clicks." },
  { slug: "vendor", icon: Handshake, title: "Vendor Performance", note: "SLA & KPI scoring monthly." },
  { slug: "ai", icon: Cpu, title: "AI Insights", note: "Predictive slippage & cost alerts." },
];

const STATS_DATA = [
  { slug: "years", value: "20+", label: "Years of enterprise transformation" },
  { slug: "programs", value: "07", label: "Programs delivered globally" },
  { slug: "industries", value: "06", label: "Industries served" },
];

// ---------- Helpers ----------
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

// ---------- Components ----------
function TopNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#050608]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-[72px]">
        <a
          href="#home"
          data-testid={NAV.brand}
          className="flex items-center gap-3 group"
        >
          <div className="relative">
            <span className="absolute inset-0 -m-1 rounded-sm border border-amber-500/0 group-hover:border-amber-500/50 transition-colors duration-300" />
            <img
              src={LOGO_URL}
              alt="PMONexa"
              className="h-12 w-12 md:h-14 md:w-14 object-cover object-center shadow-[0_8px_24px_-8px_rgba(245,158,11,0.45)] ring-1 ring-amber-500/20 group-hover:ring-amber-500/60 transition-all duration-300"
              style={{ borderRadius: 0 }}
            />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-[22px] md:text-[24px] font-semibold tracking-tight">
              PMO<span className="text-amber-500">Nexa</span>
            </span>
            <span className="hidden md:block text-[9px] tracking-[0.32em] uppercase text-slate-500 mt-1">
              Transform · Govern · Accelerate
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={l.testId}
              className="text-[13px] text-slate-300 hover:text-white transition-colors tracking-wide"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid={NAV.ctaConsult}
            className="pmx-btn-primary !py-2.5 !px-5 !text-[11px] hidden md:inline-flex"
          >
            Book a Consult <ArrowUpRight size={14} />
          </a>
          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center border border-white/10 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileMenu}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#050608]/95 backdrop-blur-xl border-t border-white/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-slate-300 hover:text-amber-500 text-sm tracking-wide flex items-center justify-between"
                >
                  {l.label}
                  <ChevronRight size={14} />
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Hero() {
  return (
    <section
      id="home"
      data-testid={HERO.section}
      className="relative pt-[140px] pb-32 md:pt-[180px] md:pb-44 overflow-hidden"
    >
      {/* Layered backgrounds */}
      <div className="absolute inset-0 pmx-grid-bg opacity-50" />
      <div className="absolute inset-0 pmx-spotlight-amber" />
      <div className="absolute inset-0 pmx-spotlight-copper" />
      <div className="pmx-noise" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="grid grid-cols-12 gap-10 items-center"
        >
          {/* LEFT — copy */}
          <div className="col-span-12 lg:col-span-7">
            <motion.div variants={fadeUp} className="flex items-center gap-4 mb-8">
              <span data-testid={HERO.eyebrow} className="pmx-eyebrow">
                PMO • Digital Transformation • Enterprise Advisory
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              data-testid={HERO.title}
              className="font-display text-[40px] sm:text-[58px] md:text-[72px] lg:text-[86px] leading-[0.96] font-medium tracking-tight"
            >
              <span className="pmx-title-stroke">Transforming Projects.</span>
              <br />
              <span className="text-white">Accelerating </span>
              <span className="pmx-amber-text italic font-light">Enterprises.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              data-testid={HERO.subtitle}
              className="mt-8 text-base md:text-lg text-slate-400 max-w-xl leading-relaxed font-light"
            >
              PMONexa is a next-generation PMO and digital transformation partner —
              fusing governance, AI-driven insight, and enterprise advisory so your
              strategic programs land on time, on budget, and on outcome.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid={HERO.ctaConsult} className="pmx-btn-primary">
                Book a Strategy Call <ArrowUpRight size={16} />
              </a>
              <a href="#services" data-testid={HERO.ctaServices} className="pmx-btn-ghost">
                Explore Services <ArrowRight size={16} />
              </a>
            </motion.div>

            {/* Inline hero stats strip */}
            <motion.div
              variants={fadeUp}
              className="mt-14 grid grid-cols-3 gap-4 max-w-xl border-t border-white/10 pt-8"
            >
              <div>
                <div className="font-display text-3xl md:text-4xl text-white">94.2<span className="text-amber-500">%</span></div>
                <div className="text-[10px] tracking-[0.22em] text-slate-500 uppercase mt-2">On-time delivery</div>
              </div>
              <div>
                <div className="font-display text-3xl md:text-4xl text-white">07</div>
                <div className="text-[10px] tracking-[0.22em] text-slate-500 uppercase mt-2">Programs under watch</div>
              </div>
              <div>
                <div className="font-display text-3xl md:text-4xl text-amber-500">20<span className="text-white">+</span></div>
                <div className="text-[10px] tracking-[0.22em] text-slate-500 uppercase mt-2">Years experience</div>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-3">
              <span className="pmx-tag"><span className="pmx-amber-dot" /> Active in 6 industries</span>
              <span className="pmx-tag">SAP / Digital / PMO</span>
              <span className="pmx-tag">Global delivery</span>
            </motion.div>
          </div>

          {/* RIGHT — animated logo centerpiece */}
          <motion.div
            variants={fadeUp}
            className="col-span-12 lg:col-span-5 flex justify-center items-center min-h-[420px] lg:min-h-[560px]"
          >
            <div className="relative w-[340px] h-[340px] sm:w-[440px] sm:h-[440px] lg:w-[520px] lg:h-[520px]">
              <HeroLogoMark />
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Marquee trust band */}
      <div data-testid={HERO.marquee} className="relative mt-24 border-y border-white/10 py-7 overflow-hidden">
        <div className="pmx-marquee gap-16 px-8 text-slate-500 text-sm tracking-[0.32em] uppercase font-medium">
          {Array.from({ length: 2 }).map((_, dup) => (
            <React.Fragment key={dup}>
              <span>Trusted by Global Enterprises</span>
              <span className="pmx-amber-dot" />
              <span>Manufacturing</span>
              <span className="pmx-amber-dot" />
              <span>Chemicals</span>
              <span className="pmx-amber-dot" />
              <span>Pharmaceuticals</span>
              <span className="pmx-amber-dot" />
              <span>Logistics</span>
              <span className="pmx-amber-dot" />
              <span>Shared Services</span>
              <span className="pmx-amber-dot" />
              <span>20+ Years of Delivery</span>
              <span className="pmx-amber-dot" />
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

function OrbitMark() {
  return (
    <svg viewBox="0 0 680 680" className="w-full h-full opacity-70">
      <defs>
        <radialGradient id="g1" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="g2" x1="0" x2="1">
          <stop offset="0%" stopColor="#EA580C" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <circle cx="340" cy="340" r="220" fill="url(#g1)" />
      {[120, 180, 250, 320].map((r, i) => (
        <circle
          key={r}
          cx="340"
          cy="340"
          r={r}
          fill="none"
          stroke="url(#g2)"
          strokeWidth="0.8"
          strokeDasharray={i % 2 === 0 ? "2 6" : "0"}
          opacity={0.55 - i * 0.08}
        />
      ))}
      {/* nodes */}
      {[
        [340, 120, 6],
        [560, 340, 7],
        [340, 560, 5],
        [120, 340, 6],
        [495, 185, 4],
        [185, 495, 4],
        [495, 495, 4],
        [185, 185, 4],
      ].map(([cx, cy, r], i) => (
        <g key={i}>
          <circle cx={cx} cy={cy} r={r + 5} fill="#F59E0B" opacity="0.12" />
          <circle cx={cx} cy={cy} r={r} fill="#F59E0B" />
        </g>
      ))}
      {/* center */}
      <circle cx="340" cy="340" r="12" fill="#EA580C" />
      <circle cx="340" cy="340" r="28" fill="none" stroke="#EA580C" strokeWidth="1" opacity="0.5" />
    </svg>
  );
}

function HeroLogoMark() {
  // Animated, professional presentation of the PMONexa brand logo.
  // Concentric rings rotate at different speeds; the logo image sits in the center
  // with a soft glow + breathing pulse.
  return (
    <div className="relative w-full h-full flex items-center justify-center select-none">
      {/* Outer halo */}
      <motion.div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(245,158,11,0.22) 0%, rgba(234,88,12,0.06) 38%, rgba(0,0,0,0) 65%)",
        }}
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Rotating outer ring with ticks */}
      <motion.svg
        viewBox="0 0 600 600"
        className="absolute inset-0 w-full h-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
      >
        <circle
          cx="300" cy="300" r="270"
          fill="none" stroke="rgba(245,158,11,0.28)" strokeWidth="1"
          strokeDasharray="2 14"
        />
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i * 30) * (Math.PI / 180);
          const x1 = 300 + Math.cos(angle) * 260;
          const y1 = 300 + Math.sin(angle) * 260;
          const x2 = 300 + Math.cos(angle) * 278;
          const y2 = 300 + Math.sin(angle) * 278;
          return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="rgba(245,158,11,0.55)" strokeWidth="1" />
          );
        })}
      </motion.svg>

      {/* Counter-rotating middle ring */}
      <motion.svg
        viewBox="0 0 600 600"
        className="absolute inset-0 w-full h-full"
        animate={{ rotate: -360 }}
        transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
      >
        <circle cx="300" cy="300" r="220" fill="none"
          stroke="rgba(234,88,12,0.35)" strokeWidth="0.8" />
        {[
          [300, 80], [520, 300], [300, 520], [80, 300],
        ].map(([cx, cy], i) => (
          <g key={i}>
            <circle cx={cx} cy={cy} r={9} fill="rgba(245,158,11,0.18)" />
            <circle cx={cx} cy={cy} r={4} fill="#F59E0B" />
          </g>
        ))}
      </motion.svg>

      {/* Inner ring (subtle) */}
      <motion.svg
        viewBox="0 0 600 600"
        className="absolute inset-0 w-full h-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      >
        <circle cx="300" cy="300" r="170" fill="none"
          stroke="rgba(245,158,11,0.5)" strokeWidth="0.6"
          strokeDasharray="1 8" />
      </motion.svg>

        {/* Center logo with breathing pulse */}
        <motion.div
          data-testid="hero-logo-mark"
          className="relative z-10"
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        >
          {/* Pulsing rings */}
          <motion.span
            className="absolute inset-0 -m-6 rounded-full border border-amber-500/40"
            animate={{ scale: [1, 1.18, 1], opacity: [0.6, 0, 0.6] }}
            transition={{ duration: 3.4, repeat: Infinity, ease: "easeOut" }}
          />
          <motion.span
            className="absolute inset-0 -m-3 rounded-full border border-amber-500/30"
            animate={{ scale: [1, 1.12, 1], opacity: [0.45, 0, 0.45] }}
            transition={{ duration: 3.4, repeat: Infinity, ease: "easeOut", delay: 0.9 }}
          />

          {/* Glow disc */}
          <span
            className="absolute inset-0 -m-2 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(245,158,11,0.55) 0%, rgba(245,158,11,0) 70%)",
              filter: "blur(20px)",
            }}
          />

          <motion.img
            src={LOGO_URL}
            alt="PMONexa"
            className="relative w-[260px] h-[260px] object-cover object-center shadow-[0_30px_80px_-20px_rgba(245,158,11,0.55)] ring-2 ring-amber-500/40"
            style={{ borderRadius: "999px" }}
            animate={{ scale: [1, 1.03, 1] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>

        {/* Brand tagline placed safely below the logo (outside the orb container) */}
        <motion.div
          className="absolute left-1/2 -translate-x-1/2 bottom-[-8px] sm:bottom-[-14px] text-center whitespace-nowrap z-10"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
        >
          <div className="font-display text-[13px] sm:text-sm text-slate-300 italic font-light tracking-wide">
            transform <span className="text-amber-500">·</span> govern <span className="text-amber-500">·</span> accelerate
          </div>
        </motion.div>
      </div>
    );
  }

function ServicesGrid() {
  return (
    <section
      id="services"
      data-testid={SERVICES.section}
      className="relative py-32 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 lg:col-span-5">
            <div className="pmx-eyebrow mb-6">What we do</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight">
              An operating system <br />
              for <span className="pmx-amber-text italic font-light">enterprise delivery.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-slate-400 text-lg leading-relaxed font-light">
              From advisory to execution to augmentation — PMONexa brings the
              governance, talent and tools to turn strategy into measurable outcomes.
              Five integrated services, one accountable partner.
            </p>
          </div>
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 lg:grid-cols-12 auto-rows-[minmax(180px,auto)] gap-5"
        >
          {SERVICES_DATA.map((s) => (
            <ServiceCard key={s.slug} service={s} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ServiceCard({ service }) {
  const Icon = service.icon;
  return (
    <motion.article
      variants={fadeUp}
      data-testid={SERVICES.card(service.slug)}
      className={`pmx-card p-8 md:p-10 group flex flex-col justify-between ${service.span}`}
    >
      <div>
        <div className="flex items-center justify-between mb-8">
          <span className="font-mono text-xs text-amber-500/80 tracking-[0.2em]">
            / {service.code}
          </span>
          <div className="w-11 h-11 border border-white/10 flex items-center justify-center text-amber-500 group-hover:bg-amber-500 group-hover:text-black transition-all">
            <Icon size={18} />
          </div>
        </div>

        <h3
          className={`font-display tracking-tight text-white ${
            service.featured
              ? "text-3xl md:text-5xl leading-[1.05]"
              : "text-xl md:text-2xl leading-tight"
          }`}
        >
          {service.title}
        </h3>
        <p
          className={`text-slate-400 leading-relaxed font-light mt-4 ${
            service.featured ? "text-base md:text-lg max-w-xl" : "text-sm"
          }`}
        >
          {service.desc}
        </p>

        <ul
          className={`mt-7 grid ${
            service.featured ? "grid-cols-2 gap-x-6 gap-y-3" : "grid-cols-1 gap-2"
          }`}
        >
          {service.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2 text-sm text-slate-300">
              <span className="mt-2 w-1 h-1 bg-amber-500 shrink-0" />
              {b}
            </li>
          ))}
        </ul>

        {service.featured && service.metrics && (
          <div className="mt-8 grid grid-cols-3 gap-4 border-t border-white/10 pt-6">
            {service.metrics.map((m) => (
              <div key={m.label}>
                <div className="font-display text-2xl md:text-3xl text-amber-500">{m.value}</div>
                <div className="text-[10px] tracking-[0.22em] text-slate-500 uppercase mt-1">{m.label}</div>
              </div>
            ))}
          </div>
        )}

        {service.featured && service.phases && (
          <div className="mt-7">
            <div className="text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-3">Our 4-D engagement</div>
            <div className="flex items-center gap-2 flex-wrap">
              {service.phases.map((p, i) => (
                <React.Fragment key={p.code}>
                  <div className="flex items-center gap-2 px-3 py-2 border border-white/10 group-hover:border-amber-500/30 transition-colors">
                    <span className="font-mono text-[10px] text-amber-500">{p.code}</span>
                    <span className="text-xs text-slate-300 font-medium tracking-wide">{p.label}</span>
                  </div>
                  {i < service.phases.length - 1 && (
                    <ArrowRight size={12} className="text-slate-600" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {service.featured && service.frameworks && (
          <div className="mt-7">
            <div className="text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-3">Frameworks we operate in</div>
            <div className="flex flex-wrap gap-2">
              {service.frameworks.map((f) => (
                <span key={f} className="pmx-tag">{f}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <a
        href="#contact"
        data-testid={SERVICES.cardCta(service.slug)}
        className="mt-8 inline-flex items-center gap-2 text-amber-500 text-[12px] tracking-[0.22em] uppercase hover:gap-3 transition-all"
      >
        Engage <ArrowUpRight size={14} />
      </a>
    </motion.article>
  );
}

function WhyPMONexa() {
  return (
    <section
      id="why"
      data-testid={WHY.section}
      className="relative py-32 md:py-40 border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-5 lg:sticky lg:top-32 self-start">
          <div className="pmx-eyebrow mb-6">Why PMONexa</div>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Twenty plus years of <br />
            <span className="pmx-amber-text italic font-light">enterprise transformation</span>
            <br /> in the room with you.
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed font-light max-w-md">
            We bring the scars and the playbooks — across SAP programs, digital
            initiatives, application rationalization, and enterprise PMO setup —
            so your team doesn&rsquo;t relearn lessons the hard way.
          </p>
          <a href="#contact" className="pmx-btn-ghost mt-8">
            Talk to a Partner <ArrowRight size={14} />
          </a>
        </div>

        <div className="col-span-12 lg:col-span-7 flex flex-col gap-5">
          {WHY_PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.slug}
                data-testid={WHY.pillar(p.slug)}
                className="pmx-card p-8 md:p-10 grid grid-cols-12 gap-6 items-center"
              >
                <div className="col-span-12 md:col-span-2">
                  <div className="font-display text-4xl md:text-5xl font-medium text-amber-500">
                    {p.metric}
                  </div>
                  <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mt-1">
                    {p.metricLabel}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-9">
                  <div className="flex items-center gap-3 mb-2 text-amber-500">
                    <Icon size={16} />
                    <span className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
                      Pillar
                    </span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl text-white tracking-tight">
                    {p.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed mt-2 font-light">{p.desc}</p>
                </div>
                <div className="hidden md:flex col-span-1 justify-end">
                  <ArrowUpRight className="text-amber-500" size={20} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function IndustriesSection() {
  return (
    <section
      id="industries"
      data-testid={INDUSTRIES.section}
      className="relative py-32 md:py-40 border-t border-white/10 bg-[#0a0d14]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 lg:col-span-7">
            <div className="pmx-eyebrow mb-6">Industries we support</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight">
              Built for the messy <br />
              middle of <span className="pmx-amber-text italic font-light">real enterprises.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 flex items-end">
            <p className="text-slate-400 leading-relaxed font-light">
              Regulated, global, multi-vendor — we operate where complexity lives.
            </p>
          </div>
        </div>

        <div className="border-b border-white/10">
          {INDUSTRIES_DATA.map((ind, i) => (
            <a
              key={ind.slug}
              href="#contact"
              data-testid={INDUSTRIES.row(ind.slug)}
              className="pmx-industry-row group flex items-center justify-between py-8 md:py-10 cursor-pointer"
            >
              <div className="flex items-center gap-8 md:gap-14">
                <span className="pmx-industry-num font-mono text-sm text-slate-600 transition-colors">
                  / {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-2xl md:text-4xl tracking-tight text-white">
                  {ind.name}
                </span>
              </div>
              <div className="flex items-center gap-6">
                <span className="hidden md:inline pmx-tag">{ind.tag}</span>
                <ArrowUpRight
                  className="pmx-industry-arrow text-amber-500 opacity-50 transition-all"
                  size={20}
                />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function PMOToolSection() {
  return (
    <section
      id="pmo-tool"
      data-testid={PMOTOOL.section}
      className="relative py-32 md:py-40 border-t border-white/10 overflow-hidden"
    >
      <div className="absolute inset-0 pmx-grid-bg opacity-40" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] pmx-spotlight-amber opacity-60" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14">
          <div className="col-span-12 lg:col-span-7">
            <div className="pmx-eyebrow mb-6">Lightweight PMO Tool</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight">
              A control room <br />
              without the <span className="pmx-amber-text italic font-light">bloat.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 flex items-end">
            <p className="text-slate-400 leading-relaxed font-light">
              Executive-ready dashboards, risk and resource intelligence — built for PMO leaders, not procurement committees.
            </p>
          </div>
        </div>

        {/* Mock dashboard */}
        <div className="pmx-card p-6 md:p-10 mb-8">
          <div className="flex items-center justify-between border-b border-white/10 pb-5 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              <div className="w-2.5 h-2.5 rounded-full bg-rose-500" />
              <span className="ml-4 font-mono text-xs text-slate-500">pmonexa.app / portfolio</span>
            </div>
            <span className="pmx-tag">Live • Q2</span>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-8 border border-white/10 p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">
                    Portfolio Health
                  </div>
                  <div className="font-display text-3xl">37 Programs · 94.2% on-track</div>
                </div>
                <ChartLine className="text-amber-500" size={20} />
              </div>
              <BarChartMock />
            </div>

            <div className="col-span-12 md:col-span-4 grid grid-cols-1 gap-4">
              <div className="border border-white/10 p-5">
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Risks Open</div>
                <div className="font-display text-3xl">14</div>
                <div className="text-xs text-rose-400 mt-1">3 high · 6 med · 5 low</div>
              </div>
              <div className="border border-white/10 p-5">
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Vendor SLA</div>
                <div className="font-display text-3xl">98.4%</div>
                <div className="text-xs text-emerald-400 mt-1">+1.2% MoM</div>
              </div>
              <div className="border border-white/10 p-5">
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Resource Utilization</div>
                <div className="font-display text-3xl">87%</div>
                <div className="text-xs text-slate-400 mt-1">Optimal band 78–88%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PMO_TOOL_FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.slug}
                data-testid={PMOTOOL.feature(f.slug)}
                className="pmx-card p-6 group"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-10 h-10 border border-white/10 flex items-center justify-center text-amber-500 group-hover:bg-amber-500 group-hover:text-black transition-all">
                    <Icon size={16} />
                  </div>
                  <span className="font-mono text-[10px] text-slate-500">FEAT</span>
                </div>
                <h4 className="font-display text-xl text-white">{f.title}</h4>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed font-light">{f.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BarChartMock() {
  const data = [62, 71, 58, 80, 74, 88, 76, 92, 84, 95, 89, 94];
  const labels = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const max = 100;
  return (
    <div className="w-full">
      <div className="flex items-end gap-2 md:gap-3 h-44">
        {data.map((v, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-2 group">
            <div className="text-[10px] font-mono text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity">
              {v}%
            </div>
            <div
              className="w-full bg-gradient-to-t from-amber-600/80 to-amber-400/20 border-t border-amber-400"
              style={{ height: `${(v / max) * 100}%` }}
            />
            <div className="text-[10px] font-mono text-slate-500">{labels[i]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsBand() {
  return (
    <section
      data-testid={STATS.section}
      className="relative py-24 border-t border-white/10 bg-[#0a0d14]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-0">
        {STATS_DATA.map((s, i) => (
          <div
            key={s.slug}
            data-testid={STATS.stat(s.slug)}
            className={`px-6 md:px-10 py-4 ${
              i < STATS_DATA.length - 1 ? "md:border-r md:border-white/10" : ""
            }`}
          >
            <div className="pmx-stat-num">{s.value}</div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mt-3">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function VisionSection() {
  return (
    <section
      id="about"
      data-testid={VISION.section}
      className="relative py-32 md:py-44 border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-2 lg:border-r lg:border-white/10 pr-6">
          <div className="pmx-eyebrow !block">Our Vision</div>
          <div className="hidden lg:block font-mono text-xs text-slate-600 mt-12">
            / about <br />
            / pmonexa <br />
            / 2026
          </div>
        </div>
        <div className="col-span-12 lg:col-span-10">
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.15] tracking-tight font-light text-slate-200">
            To become <span className="text-white font-medium">the trusted partner</span> for
            organizations seeking <span className="pmx-amber-text italic">excellence</span> in
            project delivery, operational governance and{" "}
            <span className="pmx-amber-text italic">digital transformation</span>.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Sparkles, title: "AI-driven insight", text: "Decisions backed by predictive signals, not guesswork." },
              { icon: ShieldCheck, title: "Disciplined governance", text: "Frameworks that stand up to audit and scale." },
              { icon: TrendingUp, title: "Measurable outcomes", text: "Every initiative tied to a business KPI we own with you." },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="border-t border-white/10 pt-6">
                  <Icon className="text-amber-500" size={20} />
                  <div className="mt-4 font-display text-xl">{p.title}</div>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed font-light">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", error: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: "success", error: "" });
      setForm({ name: "", email: "", company: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setStatus({ state: "error", error: String(detail) });
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative py-32 md:py-44 border-t border-white/10 overflow-hidden"
    >
      <div className="absolute inset-0 pmx-grid-bg opacity-40" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] pmx-spotlight-copper opacity-80" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-5">
          <div className="pmx-eyebrow mb-6">Let&rsquo;s build the future</div>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Start a <br />
            <span className="pmx-amber-text italic font-light">conversation.</span>
          </h2>
          <p className="text-slate-400 leading-relaxed font-light mt-6 max-w-md">
            Whether you&rsquo;re standing up a PMO, optimizing your application landscape,
            or driving an enterprise-wide transformation — we&rsquo;re ready to step in.
          </p>

          <div className="mt-12 space-y-5 text-slate-300">
            <a
              href="mailto:support@pmonexa.com"
              data-testid={FOOTER.email}
              className="flex items-center gap-4 group hover:text-amber-500 transition-colors"
            >
              <span className="w-10 h-10 border border-white/10 flex items-center justify-center group-hover:border-amber-500">
                <Mail size={16} />
              </span>
              <span className="font-mono text-sm">support@pmonexa.com</span>
            </a>
            <a
              href="tel:+918369715615"
              data-testid={FOOTER.phone}
              className="flex items-center gap-4 group hover:text-amber-500 transition-colors"
            >
              <span className="w-10 h-10 border border-white/10 flex items-center justify-center group-hover:border-amber-500">
                <Phone size={16} />
              </span>
              <span className="font-mono text-sm">+91 83697 15615</span>
            </a>
            <div className="flex items-center gap-4 text-slate-400">
              <span className="w-10 h-10 border border-white/10 flex items-center justify-center">
                <MapPin size={16} />
              </span>
              <span className="font-mono text-sm">PMONexa Consulting Services</span>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid={CONTACT.form}
          className="col-span-12 lg:col-span-7 pmx-card p-8 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Full name *</label>
              <input
                required
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Jane Doe"
                className="pmx-input"
                data-testid={CONTACT.inputName}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Email *</label>
              <input
                required
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="jane@company.com"
                className="pmx-input"
                data-testid={CONTACT.inputEmail}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Company</label>
              <input
                name="company"
                value={form.company}
                onChange={handleChange}
                placeholder="Acme Industries"
                className="pmx-input"
                data-testid={CONTACT.inputCompany}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Phone</label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+1 555 000 1234"
                className="pmx-input"
                data-testid={CONTACT.inputPhone}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Service interest</label>
              <select
                name="service"
                value={form.service}
                onChange={handleChange}
                className="pmx-input bg-[#050608]"
                data-testid={CONTACT.inputService}
              >
                <option value="">Select a service…</option>
                <option value="PMO Advisory">PMO Advisory & Consulting</option>
                <option value="PMO Tool">Lightweight PMO Tool</option>
                <option value="Vendor Management">Vendor Management</option>
                <option value="Application Rationalization">Application Rationalization</option>
                <option value="Staffing">Staffing & Resource Augmentation</option>
                <option value="Other">Other / Not sure</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Message *</label>
              <textarea
                required
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us about your transformation goals…"
                className="pmx-input"
                data-testid={CONTACT.inputMessage}
              />
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-6">
            <p className="text-xs text-slate-500 font-mono max-w-sm">
              By submitting you agree to be contacted by PMONexa. We do not share your data.
            </p>
            <button
              type="submit"
              disabled={status.state === "loading"}
              data-testid={CONTACT.submit}
              className="pmx-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status.state === "loading" ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send Message <ArrowUpRight size={14} />
                </>
              )}
            </button>
          </div>

          {status.state === "success" && (
            <div
              data-testid={CONTACT.success}
              className="mt-8 flex items-center gap-3 p-4 border border-emerald-500/30 bg-emerald-500/5 text-emerald-300"
            >
              <CheckCircle2 size={18} />
              <span className="text-sm">
                Thanks — your message landed. A partner will reach out within 1 business day.
              </span>
            </div>
          )}
          {status.state === "error" && (
            <div
              data-testid={CONTACT.error}
              className="mt-8 flex items-center gap-3 p-4 border border-rose-500/30 bg-rose-500/5 text-rose-300"
            >
              <AlertTriangle size={18} />
              <span className="text-sm">{status.error}</span>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      data-testid={FOOTER.section}
      className="border-t border-white/10 bg-[#050608]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <img
              src={LOGO_URL}
              alt="PMONexa"
              className="h-10 w-10 object-cover object-center"
              style={{ borderRadius: 0 }}
            />
            <span className="font-display text-xl font-semibold tracking-tight">
              PMO<span className="text-amber-500">Nexa</span>
            </span>
          </div>
          <p className="text-slate-400 max-w-sm leading-relaxed font-light text-sm">
            Transforming PMOs with AI, digital innovation, and enterprise advisory —
            delivered by leaders who have lived your transformation before.
          </p>
        </div>

        <div className="col-span-6 md:col-span-3">
          <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-5">Explore</div>
          <ul className="space-y-3 text-sm text-slate-300">
            <li><a href="#home" className="hover:text-amber-500">Home</a></li>
            <li><a href="#about" className="hover:text-amber-500">About</a></li>
            <li><a href="#services" className="hover:text-amber-500">Services</a></li>
            <li><a href="#pmo-tool" className="hover:text-amber-500">PMO Tool</a></li>
            <li><a href="#why" className="hover:text-amber-500">Insights</a></li>
          </ul>
        </div>

        <div className="col-span-6 md:col-span-4">
          <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-5">Contact</div>
          <ul className="space-y-3 text-sm text-slate-300">
            <li><a href="mailto:support@pmonexa.com" className="hover:text-amber-500">support@pmonexa.com</a></li>
            <li><a href="tel:+918369715615" className="hover:text-amber-500">+91 83697 15615</a></li>
            <li className="text-slate-500">PMONexa Consulting Services</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500 font-mono">
          <span>© {new Date().getFullYear()} PMONexa. All rights reserved.</span>
          <span>Transforming Projects. Accelerating Enterprises.</span>
        </div>
      </div>
    </footer>
  );
}

// ---------- App ----------
function App() {
  useEffect(() => {
    // ping backend so we know it's alive (debug only)
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <div className="App relative">
      <TopNav />
      <main>
        <Hero />
        <ServicesGrid />
        <WhyPMONexa />
        <IndustriesSection />
        <PMOToolSection />
        <StatsBand />
        <VisionSection />
        <ContactSection />
      </main>
      <Footer />
      <ViniChat />
    </div>
  );
}

export default App;

